import { FC, memo, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import moment from 'moment/moment';
import { Block } from '../../pages/pages.styled';
import { Text, Button, message, HStack } from '../../components-v4';
import { useAppDispatch, useResponsive } from '../../../../hooks';
import {
  setShowMetersResultModal,
  setShowMetersReadingsModal,
  createMetersCheckRequest,
} from '../../request-single-page-slice';
import {
  getOnLoadPublicRequestMetersState,
  getMetersData,
  getPublicId,
} from '../../selectors';
import { MIN_SKELETON_DURATION } from '../../constants';
import { MetersSkeleton } from '../skeletons';
import { Meter } from './meter';

export const Meters: FC = memo(() => {
  const { viewport } = useResponsive();
  const dispatch = useAppDispatch();
  const onLoadMeters = useSelector(getOnLoadPublicRequestMetersState);
  const publicId = useSelector(getPublicId);
  const meters = useSelector(getMetersData);

  const [showSkeleton, setShowSkeleton] = useState<boolean>(onLoadMeters);
  const [isReadingsBlocked, setIsReadingsBlocked] = useState<boolean>(false);
  const loadStartRef = useRef<number | null>(onLoadMeters ? Date.now() : null);

  const handleCreateRequest = () => {
    try {
      const now = moment();
      const data = meters
        .filter(
          (item) =>
            moment(item.check_date).isSameOrBefore(now.clone().add(3, 'months')) //Дата поверки истекает менее, чем через 3 месяца
        )
        .map((item) => ({ meter_id: item.id }));

      dispatch(
        createMetersCheckRequest({
          id: publicId,
          data: data,
        })
      )
        .then(unwrapResult)
        .then(() => {
          dispatch(setShowMetersResultModal('request'));
        });
    } catch (e) {
      message({
        type: 'error',
        text: 'Упс...Что-то пошло не так, повторите попытку',
      });
    }
  };

  const handleSendReadings = () => {
    if (meters.every((meter) => meter.is_automatic)) {
      setIsReadingsBlocked(true);

      return;
    }

    dispatch(setShowMetersReadingsModal(true));
  };

  useEffect(() => {
    if (onLoadMeters) {
      loadStartRef.current = Date.now();
      setShowSkeleton(true);
    } else if (loadStartRef.current !== null) {
      const elapsed = Date.now() - loadStartRef.current;
      const remaining = MIN_SKELETON_DURATION - elapsed;

      if (remaining > 0) {
        const timer = setTimeout(() => setShowSkeleton(false), remaining);

        return () => clearTimeout(timer);
      }

      setShowSkeleton(false);
    }
  }, [onLoadMeters]);

  if (showSkeleton) {
    return <MetersSkeleton />;
  }

  return (
    <Block id="meters">
      <Text variant="heading-3">Счётчики</Text>

      {meters.map((meter) => (
        <Meter key={meter.id} {...meter} />
      ))}

      {isReadingsBlocked && (
        <Text variant={viewport.mobile ? 'body-14' : 'body-16'} color="red.500">
          Все ваши счётчики автоматические, ручной ввод не&nbsp;нужен
        </Text>
      )}

      <HStack
        spacing={viewport.desktop ? 'base' : 'xs'}
        style={{
          flexDirection: viewport.desktop ? 'row' : 'column-reverse',
        }}
      >
        <Button
          mode="neutral"
          variant="main"
          size="l"
          onClick={handleCreateRequest}
          style={{ width: '100%' }}
        >
          Создать заявку на поверку
        </Button>
        <Button
          mode="additional"
          variant="main"
          size="l"
          onClick={handleSendReadings}
          style={{ width: '100%' }}
        >
          Передать показания
        </Button>
      </HStack>
    </Block>
  );
});
